import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { type SelectableItem, selectedItemsSelector } from '@air/redux-selected-items';

import { BOARDLESS_LOCATION_NAME } from '~/components/v2/components/shared/selectionBar/consts';
import { type FlatListItem } from '~/components/v2/components/shared/selectionBar/types';

export const useSelectedItemsList = () => {
  const selectedItems = useSelector(selectedItemsSelector);

  const itemList = useMemo(() => {
    const boardNameLookup = new Map(
      selectedItems
        .filter((item) => item.selectedBoard !== undefined)
        .map((item) => [item.selectedBoard?.id, item.selectedBoard?.name]),
    );

    const boardItemMap = new Map<string | undefined, SelectableItem[]>();
    for (const item of selectedItems) {
      const selectedBoard = item.selectedBoard;
      boardItemMap.set(selectedBoard?.id, [...(boardItemMap.get(selectedBoard?.id) || []), item]);
    }

    const entries = Array.from(boardItemMap.entries());
    const entriesWithBoardName = [];
    const entriesWithoutBoardName = [];

    for (const entry of entries) {
      const [boardId] = entry;
      const boardName = boardNameLookup.get(boardId);
      if (boardName) {
        entriesWithBoardName.push(entry);
      } else {
        entriesWithoutBoardName.push(entry);
      }
    }

    return [...entriesWithBoardName, ...entriesWithoutBoardName].flatMap(([boardId, items]) => {
      const boardName = boardNameLookup.get(boardId);

      const header: FlatListItem = {
        type: 'header',
        boardId,
        boardName: boardName ?? BOARDLESS_LOCATION_NAME,
        itemCount: items.length,
      };
      // Sort items so that boards come first
      const sortedItems = [...items].sort((a, b) => {
        const aIsBoard = a.type === 'board' || a.type === 'topLevelBoard';
        const bIsBoard = b.type === 'board' || b.type === 'topLevelBoard';

        if (aIsBoard && !bIsBoard) {
          return -1;
        }

        if (!aIsBoard && bIsBoard) {
          return 1;
        }

        return 0;
      });
      const itemEntries: FlatListItem[] = sortedItems.map((item): FlatListItem => ({ type: 'item', item }));

      return [header, ...itemEntries];
    });
  }, [selectedItems]);

  return { itemList };
};
